import { useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { SidebarSearchBox } from './SidebarSearchBox';
import { Loading } from '@framework/ui/atoms/Loading';
import { useSidebarSearch } from './useSidebarSearch';
import { useAtom } from 'jotai';
import { sidebarSearchKeywordAtom } from './sidebarSearchKeywordAtom';
import { useActionLogSender } from '@framework/action-log';
import { SearchResult } from './SearchResult/SearchResult';
import { KataSearchResult } from './KataSearchResult';

type Props = {
  isVisible: boolean;
  onClose: () => void;
  groupId: string;
};

export const SidebarSearchPanel: React.FC<Props> = ({ isVisible, onClose, groupId }: Props) => {
  const { t } = useTranslation();
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const actionLogSender = useActionLogSender();

  const { isSearching } = useSidebarSearch(groupId);

  const [searchKeyword, setSearchKeyword] = useAtom(sidebarSearchKeywordAtom);

  const handleSearch = useCallback(
    (keyword: string) => {
      setSearchKeyword(keyword);
      actionLogSender('global:sidebar:search', { keyword });
    },
    [actionLogSender, setSearchKeyword]
  );

  // パネルが表示されたら検索ボックスにフォーカスを当てる
  useEffect(() => {
    if (isVisible && searchBoxRef.current) {
      const inputElement = searchBoxRef.current.querySelector('input');
      if (inputElement) {
        inputElement.focus();
      }
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className="absolute left-full top-0 z-10 flex h-screen w-80 flex-col bg-white shadow-lg">
      <div className="flex items-center justify-between p-3">
        <div className="text-lg font-semibold">{t('common.action.search')}</div>
        <button
          className="flex size-8 items-center justify-center rounded-full hover:bg-gray-200 focus:outline-none"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className="p-3" ref={searchBoxRef}>
        <SidebarSearchBox onSearch={handleSearch} searchKeyword={searchKeyword} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-3 pl-3 text-sm">
        {searchKeyword.length > 0 ? (
          isSearching ? (
            <Loading />
          ) : (
            <>
              <SearchResult />
              <KataSearchResult />
            </>
          )
        ) : null}
      </div>
    </div>
  );
};
